import type { OptionsType } from '@shared/components';
import { RelatedPeopleStatus } from '@shared/constants/constants';
import { APPOINTMENT_PATH, APPOINTMENT_RESCHEDULE_PATH, MY_HEALTH_PROFILE_BASE_PATH } from '@shared/constants/paths';
import type { RelatedPersonProfile } from '@shared/services/caregivers/types/getRelatedPeople.types';
import type { FederatedUserProfile } from '@shared/services/personalHealth/types/getFederatedUserProfile.types';
import type { TemplateFormItem } from '@shared/types/form.types';
import type { ProfileFull } from '@shared/types/user.types';

import { HB_I_WISH_QUESTION, I_WISH_QUESTION, REQUEST_FORM_MYSELF } from '../Appointment/constants/requestForm';
import type { RequestFormTypeType } from '../AppointmentMakeNew/types/makeNew';
import { CervicalQuestionIds, RequestFormType } from '../AppointmentMakeNew/types/makeNew';

export const normaliseContactNumber = (value?: string) => (value ?? '').replace(/\s/g, '');

export const getPatientOptions = ({
  childrenProfiles,
  familyAndFriendProfiles,
  myProfile,
}: {
  childrenProfiles?: RelatedPersonProfile[];
  familyAndFriendProfiles?: RelatedPersonProfile[];
  myProfile?: FederatedUserProfile | ProfileFull;
}) => {
  const isNgemrPatient = !!(myProfile as ProfileFull)?.IsNGEMRPatient;
  const myProfileDisplayName = myProfile?.DisplayName?.trim() ? myProfile.DisplayName : undefined;
  const profilePatientName = isNgemrPatient ? myProfile?.FormattedName : undefined;

  const result = [
    {
      // Self identity is DisplayName-only (no Name fallback); consumers must not compare this against Name.
      label: myProfileDisplayName ?? REQUEST_FORM_MYSELF,
      value: myProfileDisplayName ?? REQUEST_FORM_MYSELF,
      requesterFullName: profilePatientName ?? '',
      userPrn: myProfile?.Prn ?? '',
    },
  ];

  const addApprovedProfiles = (profiles?: RelatedPersonProfile[]) => {
    profiles
      ?.filter((profile) => profile.Status === RelatedPeopleStatus.APPROVED)
      .forEach((user) => {
        result.push({
          label: user.DisplayName,
          value: user.DisplayName,
          requesterFullName: user.RequesterFullName ?? '',
          userPrn: user.Prn,
        });
      });
  };

  addApprovedProfiles(childrenProfiles);
  addApprovedProfiles(familyAndFriendProfiles);

  // Related-person values are masked NRIC display names (e.g. "SXXXX612F"), which legitimately fail
  // isValidName, so the list isn't reduced by it here; callers exclude the blank self entry by
  // comparing userPrn when the user is selecting a patient on someone else's behalf.
  return result;
};

// The fields registered by the common AddressInput. Kept as one local type so the fold helpers and
// PersonalInformationFormType don't each re-list the literals. react-ui exports ADDRESS_FIELD for the
// runtime names, but types its values as `string`, so the precise union is defined here.
export type AddressFormValues = Partial<Record<'addressStreet' | 'blockHouse' | 'floorUnit' | 'postalCode', string>>;

// AddressInput validates floorUnit against /^\d{2,}-\d{2,}$/, so pad each numeric segment to at
// least two digits — patient-master data can hold single-digit levels/units (e.g. "3-12") that
// would otherwise fail validation on mount and disable Submit on data the patient never typed.
const padFloorUnitSegment = (segment: string) => (/^\d+$/.test(segment) ? segment.padStart(2, '0') : segment);

export const formatFloorUnit = (level?: string, unit?: string): string | undefined => {
  if (!level || !unit) return undefined;
  return `${padFloorUnitSegment(level)}-${padFloorUnitSegment(unit)}`;
};

// Reverse of formatFloorUnit: split a "level-unit" value back into its parts (tolerating a leading
// '#' from legacy formatting) for payloads that carry Level/Unit separately.
export const parseFloorUnit = (value?: string): { level: string; unit: string } => {
  const normalised = value?.startsWith('#') ? value.slice(1) : (value ?? '');
  const [level = '', unit = ''] = normalised.split('-');
  return { level, unit };
};

// The common AddressInput component collects blockHouse, floorUnit and addressStreet, but the HB and
// public request form submissions must keep the BAU LinkIds (unitNumber, addressStreet).
// NOTE: spread the result AFTER the raw form values so the undefined overrides land last.
export const getLegacyHBAddressValues = (values: AddressFormValues) => {
  const fullAddress = [values?.blockHouse, values?.addressStreet].filter(Boolean).join(' ');

  return {
    addressStreet: fullAddress || undefined,
    blockHouse: undefined,
    floorUnit: undefined,
    unitNumber: values?.floorUnit,
  };
};

export const getRequestFormTypeByAppointment = ({
  canCancel,
  canReschedule,
  path,
}: {
  canCancel?: boolean;
  canReschedule?: boolean;
  path: string;
}): RequestFormTypeType => {
  // To remove trailing slashes from the path for consistent comparison
  const normalizedPath = path.replace(/\/+$/, '');

  // Specific path checks to do first as dashboard path is only '/appointments'
  // If the method is called from reschedule related pages, will always be Change type
  if (normalizedPath.includes(APPOINTMENT_RESCHEDULE_PATH)) {
    return RequestFormType.Change;
  }

  const PERSONALHEALTH_APPOINTMENT_PATH = `${MY_HEALTH_PROFILE_BASE_PATH}/appointments`;
  // If the method is called from the dashboard, the form type will be opposite of canCancel and canReschedule
  if ([APPOINTMENT_PATH, PERSONALHEALTH_APPOINTMENT_PATH].includes(normalizedPath)) {
    if (!canCancel && !canReschedule) {
      return RequestFormType.ChangeOrCancel;
    }
    if (!canCancel) {
      return RequestFormType.Cancel;
    }
    if (!canReschedule) {
      return RequestFormType.Change;
    }
    // If cancel from dashboard and got friendly error message while canceling, will be Cancel
    if (canCancel) {
      return RequestFormType.Cancel;
    }
  }

  // default will be Make as it will cover for book next and other cases
  return RequestFormType.Make;
};

export const getRequestFormTemplateType = (type: RequestFormTypeType): OptionsType[] => {
  switch (type) {
    case RequestFormType.Cancel:
      return [I_WISH_QUESTION[1]];
    case RequestFormType.Change:
      return [I_WISH_QUESTION[0]];
    default:
      return I_WISH_QUESTION;
  }
};

export const getHBRequestFormTemplateType = (type: RequestFormTypeType): OptionsType => {
  switch (type) {
    case RequestFormType.Cancel:
      return HB_I_WISH_QUESTION[2];
    case RequestFormType.Change:
    case RequestFormType.ChangeOrCancel:
      return HB_I_WISH_QUESTION[1];
    default:
      return HB_I_WISH_QUESTION[0];
  }
};

export const getPatientTypeTitle = (formType: RequestFormTypeType) => {
  if (formType === RequestFormType.Cancel) {
    return 'appointment.CancelAppointmentFor';
  }
  if (formType === RequestFormType.Change) {
    return 'appointment.RescheduleAppointmentFor';
  }
  return 'appointment.RequestNewAppointmentFor';
};

export const getFormRequiredMessage = (fieldId: string) => {
  const formRequiredMessages: Record<string, string> = {
    [CervicalQuestionIds.contactNumber]: 'Please provide the Handphone number.',
    [CervicalQuestionIds.emailAddress]: 'Please enter email address.',
    default: 'This field is required.',
    'MMG-Q004': 'Please enter patient name.',
    'MMG-Q005': 'Please provide the Handphone number.',
    'MMG-Q005-1': 'Retype the Handphone number.',
    'MMG-Q006': 'Please enter email address.',
    'MMG-Q006-1': 'Please enter email address.',
  };
  return formRequiredMessages[fieldId] || formRequiredMessages.default;
};

export const getOptionalFields = (templates: TemplateFormItem[]): string[] =>
  templates.filter((template) => !template.Required).map((item) => item.LinkId);
